import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import "./ResetPasswordModal.scss";
import UserInfo from "@utils/UserInfo";

/**
 * Forced password reset overlay.
 *
 * Shown on every authenticated page when `user_info.resetPassword === true`
 * (i.e. admin.reset_password = 1 in DB). Cannot be dismissed - the user must
 * set a new password to continue using the app.
 */
const ResetPasswordModal = ({ t }) => {
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  // Decide visibility from localStorage on mount
  useEffect(() => {
    try {
      const info = UserInfo.getUser();
      if (!info) return;
      if (info && info.resetPassword === true) {
        setVisible(true);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Block ESC key while modal is open
  useEffect(() => {
    if (!visible) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [visible]);

  const validate = () => {
    const next = {};
    if (!password) {
      next.password = t("RESET_PASSWORD_ERROR_REQUIRED");
    } else if (password.length < 8) {
      next.password = t("RESET_PASSWORD_ERROR_MIN_LENGTH");
    }
    if (!confirmPassword) {
      next.confirmPassword = t("RESET_PASSWORD_ERROR_REQUIRED");
    } else if (password && confirmPassword && password !== confirmPassword) {
      next.confirmPassword = t("RESET_PASSWORD_ERROR_MISMATCH");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;

    setSubmitting(true);
    try {
      await axios.post("/general/admin/changePassword", {
        password,
        passwordConfirmation: confirmPassword,
      });

      // Clear the flag in localStorage so this modal won't reappear on
      // navigation/refresh in the same session.
      try {
        const info = UserInfo.getUser();
        if (info) {
          info.resetPassword = false;
          localStorage.setItem("user_info", JSON.stringify(info));
        }
      } catch (_) {
        // ignore
      }

      setVisible(false);
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length) {
        setServerError(apiErrors.map((er) => er.message).join(" "));
      } else {
        setServerError(t("RESET_PASSWORD_ERROR_GENERIC"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="reset-password-modal__overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className="reset-password-modal__card">
        <div className="reset-password-modal__header">
          {t("RESET_PASSWORD_TITLE")}
        </div>
        <form className="reset-password-modal__body" onSubmit={handleSubmit}>
          <label className="reset-password-modal__label">
            {t("RESET_PASSWORD_NEW")} <span className="required">*</span>
          </label>
          <input
            type="password"
            className={`reset-password-modal__input ${
              errors.password ? "has-error" : ""
            }`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            disabled={submitting}
          />
          {errors.password && (
            <div className="reset-password-modal__field-error">
              {errors.password}
            </div>
          )}

          <label className="reset-password-modal__label">
            {t("RESET_PASSWORD_CONFIRM")} <span className="required">*</span>
          </label>
          <input
            type="password"
            className={`reset-password-modal__input ${
              errors.confirmPassword ? "has-error" : ""
            }`}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={submitting}
          />
          {errors.confirmPassword && (
            <div className="reset-password-modal__field-error">
              {errors.confirmPassword}
            </div>
          )}

          {serverError && (
            <div className="reset-password-modal__server-error">
              {serverError}
            </div>
          )}

          <div className="reset-password-modal__footer">
            <button
              type="submit"
              className="reset-password-modal__validate"
              disabled={submitting}
            >
              {submitting ? (
                <ClipLoader size={14} color="#ffffff" />
              ) : (
                t("VALIDATE")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default withTranslation()(ResetPasswordModal);
